/**
 * Rodapé e vinílico reconhecidos NO FRONT — S12.
 *
 * O item do orçamento não traz a classe do produto (`productClass.code`), e
 * trazê-la mexeria na API, fora do escopo da S12. O que o item tem é o que basta
 * para as duas perguntas que a tela faz (de onde saiu o preço, e se vale
 * conferir a tabela antes de emitir):
 *
 * - **rodapé**: vendido por `barra` (S11) **ou** referência `RP`. O prefixo
 *   cobre o rodapé novo, que nasce `caixa` até alguém configurar a barra
 *   (S11, D5) — sem ele, justo o rodapé recém-importado escaparia.
 * - **vinílico**: referência `LVT` ou `SPC`, o mesmo prefixo que a importação
 *   dedicada já usa no servidor para separar piso de rodapé.
 *
 * É regra de catálogo do tenant #1 escrita na tela, e por isso fica só aqui:
 * nada de preço depende dela — ela escolhe um texto e abre (ou não) um aviso.
 */

interface ItemComReferencia {
  referencia: string;
  unidadeVenda?: 'caixa' | 'barra' | undefined;
  precoManual?: boolean | undefined;
}

export function ehRodape(item: ItemComReferencia): boolean {
  return item.unidadeVenda === 'barra' || /^RP/i.test(item.referencia.trim());
}

export function ehVinilico(item: Pick<ItemComReferencia, 'referencia'>): boolean {
  return /^(LVT|SPC)/i.test(item.referencia.trim());
}

interface OrcamentoParaConferir<I extends ItemComReferencia> {
  tabela: string;
  rtPercentManual?: string | null | undefined;
  rtPercentPadrao?: string | null | undefined;
  itens: readonly I[];
}

/**
 * O cenário do ORC-2026-0053, que pede confirmação antes de emitir — S12, CA-6.
 *
 * Engenharia (CPF) com RT efetivo ZERO e algum rodapé ou vinílico: é como se
 * monta, por engano, a revenda da tabela com RT — e sai o preço de engenharia,
 * sem RT nenhum. Devolve um desses itens (o exemplo do aviso), ou `undefined`
 * quando não há o que conferir.
 *
 * O exemplo prefere o primeiro com preço de TABELA: um preço digitado à mão não
 * é "preço de engenharia", e citá-lo como tal no aviso seria dizer o falso. Só
 * com manuais o aviso continua valendo (a tabela é a mesma), e quem mostra
 * decide omitir o número.
 *
 * O RT efetivo é o mesmo `override ?? padrão` do servidor. Sem nenhum dos dois
 * a resposta é "não acusa": ausência não é zero, e um aviso que dispara por
 * falta de dado treina o vendedor a ignorá-lo.
 */
export function itemQuePedeConferencia<I extends ItemComReferencia>(
  orcamento: OrcamentoParaConferir<I>,
): I | undefined {
  if (orcamento.tabela !== 'CPF') return undefined;
  const rt = orcamento.rtPercentManual ?? orcamento.rtPercentPadrao;
  if (rt === null || rt === undefined || Number(rt) !== 0) return undefined;
  const candidatos = orcamento.itens.filter((i) => ehRodape(i) || ehVinilico(i));
  return candidatos.find((i) => i.precoManual !== true) ?? candidatos[0];
}
